import { useState, useEffect, useRef, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { toLocalIso } from "../utils/localDate";
import type { ChangeEvent, CSSProperties } from "react";
import { Search, Activity, Users, Clock, CalendarRange } from "lucide-react";
import { supabase } from "../supabase";
import { useQuery } from "@tanstack/react-query";
import { supabaseQuery } from "../lib/supabaseQuery";
import {
  CustomDateInput,
  MultiSelectDropdown,
} from "../utils.tsx";
import { useVirtualizer } from "@tanstack/react-virtual";

// --- SYSTEM LOGS VIEW ---

interface LogEntry {
  id: string;
  action: string;
  userName: string | null;
  userId: string | null;
  details: string | null;
  timestamp?: { toDate: () => Date } | null;
  __dateKey?: string;
}

/**
 * Row renderer for the @tanstack/react-virtual virtualized log list.
 * Only receives `data`, `index`, `style`, `t` — no closures needed.
 */
export const LogRow = memo(function LogRow({
  data,
  index,
  style
}: {
  data: LogEntry[];
  index: number;
  style: CSSProperties;
}) {
  const { t } = useTranslation();
  const log = data[index];
  if (!log) return null;
  return (
    <div style={style} className="pb-3">
      <div className="bg-white dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/80 rounded-2xl p-4 md:p-5 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 group shadow-xs hover:border-slate-300 dark:hover:border-slate-600 transition-all">
        <div className="flex-1 w-full min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-2.5">
            <span className="text-xs font-black uppercase tracking-wider px-3 py-1.5 bg-[#859F4A]/15 dark:bg-[#859F4A]/25 text-[#5e7232] dark:text-[#a6c466] border border-[#859F4A]/30 rounded-xl shadow-2xs">
              {log.action}
            </span>
            <span className="text-sm font-black text-slate-900 dark:text-white display-font truncate">
              {log.userName || t("systemLogs.system", "System")}
            </span>
            <span className="text-xs font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider ml-auto shrink-0">
              ID: {log.id.substring(0, 8)}...
            </span>
          </div>
          <p className="text-xs md:text-sm text-slate-700 dark:text-slate-200 font-medium leading-relaxed bg-slate-50/80 dark:bg-slate-900/60 p-3.5 rounded-xl border border-slate-200/60 dark:border-slate-700/60 group-hover:border-slate-300 dark:group-hover:border-slate-600 transition-all wrap-break-word">
            {log.details}
          </p>
        </div>
        <div className="flex items-center gap-2.5 px-4 py-2.5 bg-slate-100 dark:bg-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-600/80 shrink-0 w-full xl:w-auto justify-end">
          <Clock size={15} className="text-[#859F4A] shrink-0" />
          <span className="text-xs font-black text-slate-800 dark:text-slate-200 uppercase tracking-wider">
            {log.timestamp?.toDate
              ? log.timestamp.toDate().toLocaleString("de-DE")
              : t("systemLogs.recent", "Kürzlich")}
          </span>
        </div>
      </div>
    </div>
  );
});

/**
 * View component for displaying, searching, and filtering system audit logs from Firestore.
 * Provides filters for actions, users, and date ranges, with lazy load batching.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.user - Current user object containing tenant context for Firestore queries.
 * @returns {JSX.Element} The rendered SystemLogsView component.
 */
export function SystemLogsView({ user }: { user: { tenantId: string | null; id?: string; name?: string; email?: string } | null }) {
  const { t } = useTranslation();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterActions, setFilterActions] = useState<string[]>([]);
  const [filterUsers, setFilterUsers] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [extraLimit, setExtraLimit] = useState(0);
  const [prevFiltersKey, setPrevFiltersKey] = useState("");
  const listMeasureRef = useRef<HTMLDivElement | null>(null);
  const [listHeight, setListHeight] = useState(600);

  const hasActiveFilters = debouncedSearch || filterActions.length > 0 || filterUsers.length > 0 || dateFrom || dateTo;

  // Reset the "load more" offset whenever any filter changes. Uses the
  // "store previous value" pattern: compare during render and only setState
  // when the key actually changes, so React bails out of the re-render.
  const currentFiltersKey = `${dateFrom}_${dateTo}_${debouncedSearch}_${filterActions.join(",")}_${filterUsers.join(",")}`;
  if (currentFiltersKey !== prevFiltersKey) {
    setPrevFiltersKey(currentFiltersKey);
    setExtraLimit(0);
  }

  const initialLimit = hasActiveFilters ? 1000 : 50;
  const queryLimit = initialLimit + extraLimit;
  const loadBatchSize = hasActiveFilters ? 500 : 50;

  // Debounce search input to avoid querying Firestore on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch full team members/users list to populate the dropdown filter completely.
  // Migrated from a manual useEffect fetch to TanStack Query for caching/invalidation.
  const { data: userRows } = useQuery({
    queryKey: ['users', 'log-filter-names', user?.tenantId],
    queryFn: supabaseQuery(db => db.from('users').select('name, data').eq('tenant_id', user?.tenantId).limit(150)),
    enabled: !!user,
  });
  const dbUserNames = useMemo(
    () => (userRows || []).map(r => r.name || (r.data && r.data.userName)).filter(Boolean),
    [userRows]
  );

  // Measure the available height for the virtualized log list container.
  useEffect(() => {
    const el = listMeasureRef.current;
    if (!el) return;
    const update = () => setListHeight(el.clientHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load logs from Supabase. The search term is pushed SERVER-SIDE via ilike
  // (global_logs can hold tens of thousands of rows per tenant — fetching up
  // to queryLimit wide rows and then filtering client-side both wasted
  // bandwidth and silently truncated the visible result set to the newest N
  // rows regardless of what matched). Date filtering stays client-side
  // because `timestamp` is a jsonb/Firestore-legacy field parsed with
  // .toDate() (see __dateKey below) and cannot be compared in SQL directly.
  useEffect(() => {
    if (!user) return;
    const activeTenantId = user.tenantId;

    const fetchLogs = async () => {
      let query = supabase
        .from('global_logs')
        .select('*')
        .eq('tenant_id', activeTenantId)
        .order('created_at', { ascending: false });

      if (dateFrom) {
        query = query.gte('created_at', new Date(dateFrom + "T00:00:00").toISOString());
      }
      if (dateTo) {
        query = query.lte('created_at', new Date(dateTo + "T23:59:59.999").toISOString());
      }

      if (debouncedSearch) {
        // Server-side ilike over the same columns the client filter checks.
        // global_logs uses snake_case (user_name / user_id / details) — the
        // legacy Firestore camelCase names caused PostgREST 400 errors.
        // pattern `ilike.*<term>*` is case-insensitive containment — mirror
        // of .toLowerCase().includes(). .or() must be a single string of
        // comma-separated criteria.
        const escaped = debouncedSearch
          .replace(/\\/g, '\\\\')
          .replace(/[%,()]/g, '');
        query = query.or(
          `user_name.ilike.%${escaped}%,user_id.ilike.%${escaped}%,details.ilike.%${escaped}%`
        );
      }

      // Limit logic unchanged: bounds the query at queryLimit (default 1000)
      // rows AFTER the server-side filter, so the window always holds the
      // newest matches rather than the newest rows overall.
      query = query.limit(queryLimit);

      const { data: rows } = await query;
      // Postgres returns snake_case columns (user_id / user_name / created_at)
      // while the view's LogEntry shape is the legacy Firestore camelCase
      // (userId / userName / timestamp). Map on read so the client-side
      // filter and LogRow renderer keep working unchanged. created_at is an
      // ISO string — wrap it in the { toDate() } shim LogEntry expects.
      const mapped = (rows || []).map((r: Record<string, unknown>) => ({
        ...r,
        userName: (r.user_name as string) ?? null,
        userId: (r.user_id as string) ?? null,
        timestamp: r.created_at ? { toDate: () => new Date(r.created_at as string) } : null,
      })) as unknown as LogEntry[];
      setLogs(mapped);
    };
    fetchLogs();

    // Realtime handled by centralized realtimeStore
  }, [queryLimit, user, dateFrom, dateTo, debouncedSearch, filterActions, filterUsers]);

  const uniqueActions = (() => {
    const actions = logs.map((l) => l.action).filter(Boolean);
    return Array.from(new Set(actions)).sort();
  })();

  const uniqueUsers = (() => {
    const fromLogs = logs.map((l) => l.userName || "System");
    return Array.from(new Set([...dbUserNames, ...fromLogs, "System"])).sort();
  })();

  const filteredLogs = useMemo(() => {
    // Use the debounced search term so we don't re-filter on every keystroke.
    const searchLower = debouncedSearch.toLowerCase();
    return logs.filter((log) => {
      const matchesSearch =
        !debouncedSearch ||
        (log.userName && log.userName.toLowerCase().includes(searchLower)) ||
        (log.userId && log.userId.toLowerCase().includes(searchLower)) ||
        (log.details && log.details.toLowerCase().includes(searchLower));

      const matchesActions =
        filterActions.length === 0 || filterActions.includes(log.action);

      const matchesUsers =
        filterUsers.length === 0 || filterUsers.includes(log.userName || "System");

      let matchesDate = true;
      if (log.timestamp) {
        // Cache the YYYY-MM-DD string per log so we don't call .toDate() and
        // .toISOString() on every render for every row.
        const logDate = log.__dateKey || (log.__dateKey = toLocalIso(log.timestamp.toDate()));
        if (dateFrom && logDate < dateFrom) matchesDate = false;
        if (dateTo && logDate > dateTo) matchesDate = false;
      }

      return matchesSearch && matchesActions && matchesUsers && matchesDate;
    });
  }, [logs, debouncedSearch, filterActions, filterUsers, dateFrom, dateTo]);

  // Virtualized log list via @tanstack/react-virtual
  const logListRef = useRef(null);
  // TanStack Virtual intentionally returns non-memoizable functions; the
  // React Compiler advisory is expected and safe to skip here.
  // eslint-disable-next-line react-hooks/incompatible-library
  const logVirtualizer = useVirtualizer({
    count: filteredLogs.length,
    getScrollElement: () => logListRef.current,
    estimateSize: () => 110,
    overscan: 5,
  });

  return (
    <div className="space-y-6 animate-in fade-in h-[calc(100dvh-80px)] lg:h-[calc(100vh-144px)] overflow-hidden flex flex-col p-2 md:p-0">
      <header className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 shrink-0 px-4 md:px-0">
        <div>
          <h2 className="text-3xl md:text-4xl display-font font-black text-slate-900 dark:text-white tracking-tight">
            {t("systemLogs.title", "System Logs")}
          </h2>
          <p className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest mt-1.5 ml-1">
            {t("systemLogs.subtitle", "Live-Aktivitäten filtern und auswerten.")}
          </p>
        </div>
      </header>

      <div className="bg-white dark:bg-slate-800/90 border border-slate-200/80 dark:border-slate-700/80 rounded-3xl md:rounded-[2.5rem] shadow-xs flex flex-col flex-1 mx-2 md:mx-0 overflow-hidden min-h-0">
        <div className="p-5 md:p-6 border-b border-slate-200 dark:border-slate-700 flex flex-wrap items-center gap-4 bg-slate-50/60 dark:bg-slate-900/40 z-20 shrink-0">
          <div className="relative flex-1 min-w-[240px]">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
            />
            <input
              type="text"
              placeholder={t("systemLogs.searchPlaceholder", "Logs nach Nutzer, ID oder Details suchen")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 text-xs font-bold uppercase tracking-wider bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 outline-hidden focus:ring-2 focus:ring-slate-400/30 transition-all"
            />
          </div>

          <MultiSelectDropdown
            options={uniqueActions}
            selected={filterActions}
            onChange={setFilterActions}
            placeholder={t("systemLogs.activities", "Aktivitäten")}
            icon={Activity}
            translateLabel={undefined}
          />

          <MultiSelectDropdown
            options={uniqueUsers}
            selected={filterUsers}
            onChange={setFilterUsers}
            placeholder={t("systemLogs.users", "Benutzer")}
            icon={Users}
            translateLabel={undefined}
          />

          <div className="flex items-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 gap-3 shadow-2xs">
            <CalendarRange
              size={16}
              className="text-[#859F4A] shrink-0"
            />
            <div className="flex items-center gap-2">
              <CustomDateInput
                value={dateFrom}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDateFrom(e.target.value)}
                min={undefined}
                max={undefined}
                placeholder={undefined}
                required={undefined}
                className="bg-transparent text-xs font-bold uppercase tracking-wider outline-hidden text-slate-900 dark:text-white w-[95px] p-0"
              />
              <span className="text-slate-400 dark:text-slate-500 font-bold">
                /
              </span>
              <CustomDateInput
                value={dateTo}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setDateTo(e.target.value)}
                min={undefined}
                max={undefined}
                placeholder={undefined}
                required={undefined}
                className="bg-transparent text-xs font-bold uppercase tracking-wider outline-hidden text-slate-900 dark:text-white w-[95px] p-0"
              />
            </div>
          </div>
        </div>

        <div ref={listMeasureRef} className="flex-1 min-h-0 flex flex-col p-4 md:p-6 overflow-hidden">
          {filteredLogs.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-20 opacity-40 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl min-h-0 text-slate-500 dark:text-slate-400">
              <Search size={48} className="mb-4 text-slate-400 dark:text-slate-500" />
              <p className="font-bold text-sm uppercase tracking-wider text-slate-700 dark:text-slate-300">
                {t("systemLogs.noLogs", "Keine Logs gefunden")}
              </p>
            </div>
          ) : (
            <div ref={logListRef} style={{ height: listHeight ? listHeight - 32 : 550, overflow: 'auto' }} className="custom-scrollbar flex-1 pr-1">
              <div style={{ height: `${logVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                {logVirtualizer.getVirtualItems().map(vr => (
                  <LogRow
                    key={vr.key}
                    data={filteredLogs}
                    index={vr.index}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${vr.start}px)`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {logs.length >= queryLimit && (
            <div className="mt-4 shrink-0">
              <button
                onClick={() => setExtraLimit((prev) => prev + loadBatchSize)}
                className="w-full py-3 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/80 text-slate-800 dark:text-slate-200 font-bold text-xs uppercase tracking-wider rounded-xl border border-slate-200 dark:border-slate-700 transition-all shadow-xs cursor-pointer"
              >
                {t("systemLogs.loadMore", `Weitere ${loadBatchSize} Einträge laden`, { count: loadBatchSize })}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
